import { createContext, PropsWithChildren, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';
import { Call, Device } from '@twilio/voice-sdk';
import { Socket } from 'socket.io-client';

import { ClientResponseDto, ConnectedAgent, UserAvailabilityListResponseDto } from '@bandido/shared/dtos';
import { AgentStatus, WsEventsUsers } from '@bandido/shared/constants';

import { CONFIG } from '@frontend/config';
import { client } from '@frontend/features/common/services';
import { useAuth } from '@frontend/features/auth/hooks/useAuth';
import IncomingCallModal from '@frontend/features/calls/components/IncomingCallModal';
import { FloatingCallWindow } from '@frontend/features/calls/components/FloatingCallWindow';
import { useUserStatusChange } from '@frontend/features/users/queries';
import { QueryKeys } from '@frontend/features/common/types';
import { useWS } from '@frontend/features/common/hooks/useWS';
import OutgoingCallModal from '@frontend/features/calls/components/OutgoingCallModal';
import { CallAssignmentDrawer } from '@frontend/features/calls/components/CallAssignmentDrawer';
import { useNotifications } from '@frontend/contexts/NotificationContext';
import phoneRingSound from '@frontend/assets/sounds/phone-ring.mp3';
import { ClientData } from '@frontend/features/calls/components/OutgoingCallModal/types';
import { getValidCallRouteId } from '@frontend/features/calls/queries';
import { useSnackBar } from '@frontend/features/common/components/SnackBarContext';

export type CallData = {
  callSid: string;
  agencyId: number;
  calleeInfo: ClientResponseDto;
  isJoining: boolean;
  direction: 'inbound' | 'outbound';
  agentId: string;
  callId?: string;
  calledAgentId?: string;
  to: string;
  from: string;
  caseId?: number;
};

export const getCallAssignmentDiagnostics = (data?: Partial<CallData>, source = 'unknown') => ({
  source,
  callSid: data?.callSid,
  direction: data?.direction,
  transferStatus: data?.isJoining ? 'joining' : data?.calledAgentId ? 'called-agent' : 'primary',
  caseId: data?.caseId,
  agentId: data?.agentId,
  calledAgentId: data?.calledAgentId,
  hasCallId: Boolean(data?.callId),
});

export type TwilioContextState = {
  connection?: Call;
  connectedAgents: ConnectedAgent[];
  handleCall: (to: string, customParams?: Record<string, string>) => void;
  handleOpenOutgoingCallModal: (caseId?: number, clientData?: ClientData) => void;
  callsSocketRef?: Socket;
  isTransferCompleted: boolean;
  isCallTransferMode: boolean;
  callDuration: number;
  callData?: CallData;
  caseId?: number;
  sendDtmfTones: (digits: string) => Promise<void>;
  closeInProgressCall: () => void;
  updateCallData: (nextCallData: Partial<CallData>) => void;
};

export const TwilioContext = createContext<TwilioContextState>({
  connection: undefined,
  handleCall: () => { },
  connectedAgents: [],
  handleOpenOutgoingCallModal: () => { },
  callsSocketRef: undefined,
  isTransferCompleted: false,
  isCallTransferMode: false,
  callDuration: 0,
  callData: undefined,
  caseId: undefined,
  sendDtmfTones: async () => { },
  closeInProgressCall: () => { },
  updateCallData: () => { },
});

export const TwilioContextProvider = ({ children }: PropsWithChildren) => {
  const { t } = useTranslation();
  const { addToast } = useSnackBar();
  const [connectedAgents, setAvailableAgents] = useState<ConnectedAgent[]>([]);
  const [isCallInProgress, setIsCallInProgress] = useState(false);
  const [showOutgoingCallModal, setShowOutgoingCallModal] = useState(false);
  const [isSelectingCallCase, setIsSelectingCallCase] = useState(false);
  const [callDuration, setCallDuration] = useState(0);
  const [callData, setCallData] = useState<CallData>();
  const [showInboundCall, setShowInboundCall] = useState(false);
  const [connection, setConnection] = useState<Call>();
  const [caseId, setCaseId] = useState<number>();
  const [clientData, setClientData] = useState<ClientData>();
  const {
    showCallNotification,
    hasAudioPermission,
    resumeAudioContext,
    audioContext,
    setPendingRingtone
  } = useNotifications();

  const { isAuthenticated, myProfile } = useAuth();
  const queryClient = useQueryClient();

  const audioRef = useRef<HTMLAudioElement | null>(null);
  const deviceRef = useRef<Device | null>(null);
  const audioBufferRef = useRef<AudioBuffer | null>(null);
  const audioSourceRef = useRef<AudioBufferSourceNode | null>(null);
  const acceptedCallDataRef = useRef<CallData>();

  useEffect(() => {
    const loadRingtone = async () => {
      if (audioContext && !audioBufferRef.current) {
        try {
          const response = await fetch(phoneRingSound);
          const arrayBuffer = await response.arrayBuffer();
          audioBufferRef.current = await audioContext.decodeAudioData(arrayBuffer);
        } catch (error) {
          console.error('Error cargando ringtone:', error);
        }
      }
    };
    loadRingtone();
  }, [audioContext]);

  const playRingtone = useCallback(async (): Promise<boolean> => {
    if (audioContext && audioBufferRef.current) {
      try {
        const resumed = await resumeAudioContext();

        if (resumed) {
          if (audioSourceRef.current) {
            audioSourceRef.current.stop();
            audioSourceRef.current.disconnect();
          }

          const source = audioContext.createBufferSource();
          source.buffer = audioBufferRef.current;
          source.loop = true;
          source.connect(audioContext.destination);
          source.start();
          audioSourceRef.current = source;

          console.log('Ringtone reproduciendo con Web Audio API');
          return true;
        }
      } catch (error) {
        console.error('Error con Web Audio API:', error);
      }
    }

    if (audioRef.current && hasAudioPermission) {
      try {
        audioRef.current.currentTime = 0;
        audioRef.current.loop = true;
        await audioRef.current.play();
        console.log('Ringtone reproduciendo con elemento HTML');
        return true;
      } catch (error) {
        console.error('Error playing ringtone:', error);
        return false;
      }
    }

    console.log('No se pudo reproducir el ringtone - esperando interacción del usuario');
    return false;
  }, [audioContext, hasAudioPermission, resumeAudioContext]);

  const stopRingtone = useCallback(() => {
    if (audioSourceRef.current) {
      try {
        audioSourceRef.current.stop();
        audioSourceRef.current.disconnect();
        audioSourceRef.current = null;
      } catch (error) {
        console.error('Error deteniendo Web Audio source:', error);
      }
    }

    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      audioRef.current.loop = false;
    }
  }, []);

  const openCallAssignmentDrawer = useCallback(
    (nextCallData?: CallData) => {
      if (!getValidCallRouteId(nextCallData?.callId)) {
        console.warn(
          'Call assignment blocked: missing valid CRM call UUID',
          getCallAssignmentDiagnostics(nextCallData, 'open-assignment-drawer'),
        );
        addToast({
          message: t('calls.assignment.missingCallId'),
          color: 'error',
        });
        return;
      }

      setIsSelectingCallCase(true);
    },
    [addToast, t],
  );

  const updateCallData = useCallback((nextCallData: Partial<CallData>) => {
    setCallData((currentCallData) => (
      currentCallData ? { ...currentCallData, ...nextCallData } : currentCallData
    ));
  }, []);

  const { mutate: userStatusMutation } = useUserStatusChange(myProfile?.id, {
    onSettled: () => {
      if (showInboundCall) {
        stopRingtone();
        setShowInboundCall(false);
        setIsCallInProgress(true);

        const acceptedCallData = acceptedCallDataRef.current ?? callData;
        acceptedCallDataRef.current = undefined;

        if (!acceptedCallData?.isJoining) openCallAssignmentDrawer(acceptedCallData);
      } else {
        acceptedCallDataRef.current = undefined;
        stopRingtone();
        setConnection(undefined);
        setShowInboundCall(false);
        setIsCallInProgress(false);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QueryKeys.getAllUsers });
      queryClient.invalidateQueries({ queryKey: QueryKeys.getMyUserData });
    },
  });

  const { socketRef: userSocketRef } = useWS(`${CONFIG.WS_URL}/user`);
  const { socketRef: callsSocketRef } = useWS(`${CONFIG.WS_URL}/calls`);

  const cancelOutgoingCall = useCallback(
    (call: Call) => {
      call.disconnect();
      setConnection(undefined);
      setIsCallInProgress(false);
    },
    [callsSocketRef.current, myProfile?.id],
  );

  const handleOutgoingCallEvents = useCallback(
    (call: Call) => {
      setConnection(call);

      call.on('cancel', () => {
        cancelOutgoingCall(call);
      });

      call.on('disconnect', () => {
        cancelOutgoingCall(call);
      });

      call.on('error', (error: any) => {
        console.error('Outgoing call error:', error);
        cancelOutgoingCall(call);
      });
    },
    [cancelOutgoingCall],
  );

  const handleCloseCallAssignmentDrawer = useCallback(() => {
    setIsSelectingCallCase(false);
  }, []);

  const handleMakeCall = useCallback(
    (to: string) => {
      if (!myProfile?.id || !caseId) return;

      callsSocketRef.current?.emit(
        'outgoing-call:start',
        JSON.stringify({
          userId: myProfile.id,
          caseId,
          to,
        }),
      );
    },
    [callsSocketRef.current, myProfile?.id, caseId],
  );

  const handleOutgoingCallStarted = useCallback(
    (data: string) => {
      const { userId, to, caseId } = JSON.parse(data);

      if (!deviceRef.current) return;

      deviceRef.current
        .connect({
          params: {
            To: to,
            CustomParams: JSON.stringify({
              userId,
              caseId,
            }),
          },
        })
        .then(handleOutgoingCallEvents)
        .catch((error) => {
          console.error('Error making call:', error);
          setConnection(undefined);
          setIsCallInProgress(false);
        });
    },
    [deviceRef.current, handleOutgoingCallEvents],
  );

  const handleAnswerIncomingCall = useCallback((nextCallData?: CallData) => {
    const acceptedCallData = nextCallData ?? callData;
    if (!connection || !acceptedCallData) return;

    stopRingtone();

    setCallDuration(Date.now());
    setCallData(acceptedCallData);
    acceptedCallDataRef.current = acceptedCallData;

    connection.accept();

    userStatusMutation(AgentStatus.IN_CALL);
  }, [connection, callData, userStatusMutation, stopRingtone]);

  const handleCancelCall = useCallback(() => {
    stopRingtone();
    connection?.disconnect();
    setCallData(undefined);
    setIsCallInProgress(false);
    setShowInboundCall(false);
    setConnection(undefined);
  }, [connection, stopRingtone]);

  const handleDisconnectCall = useCallback(() => {
    stopRingtone();
    connection?.disconnect();
    setConnection(undefined);
    userStatusMutation(AgentStatus.AFTER_CALL);
  }, [connection, userStatusMutation, stopRingtone]);

  const handleIncomingCall = useCallback(
    (connection: Call) => {
      setConnection(connection);

      connection.on('disconnect', handleDisconnectCall);
      connection.on('cancel', handleCancelCall);
      connection.on('reject', handleCancelCall);
      connection.on('error', handleCancelCall);

      return () => {
        connection.off('disconnect', handleDisconnectCall);
        connection.off('cancel', handleCancelCall);
        connection.off('reject', handleCancelCall);
        connection.off('error', handleCancelCall);
      };
    },
    [handleDisconnectCall, handleCancelCall],
  );

  const handleTokenWillExpire = useCallback((device: Device) => {
    client.get('/calls/token').then(({ data }) => {
      device.updateToken(data.token);
    });
  }, []);

  const toggleOutgoingCallModal = useCallback(
    (next: boolean) => (caseId?: number, clientData?: ClientData) => {
      setShowOutgoingCallModal(next);
      if (!next) {
        setCaseId(undefined);
        setClientData(undefined);
      } else {
        setCaseId(caseId);
        setClientData(clientData);
      }
    },
    [],
  );

  const closeInProgressCall = useCallback(() => {
    setIsCallInProgress(false);
    setConnection(undefined);
  }, []);

  const handleStatusUpdated = useCallback(
    (userData: string) => {
      const updateItem = JSON.parse(userData) as UserAvailabilityListResponseDto;
      if (updateItem.userId === myProfile?.id) {
        queryClient.invalidateQueries({ queryKey: QueryKeys.getMyUserData });
      }
    },
    [myProfile?.id, queryClient],
  );

  const handleAvailableAgents = useCallback((agents: string) => {
    const agentsObj = JSON.parse(agents);
    const agentsList: ConnectedAgent[] = Object.values(agentsObj);
    setAvailableAgents(agentsList);
  }, []);

  const parseCallDataEvent = useCallback((data: string, source: string): CallData | undefined => {
    try {
      return JSON.parse(data);
    } catch (error) {
      console.error('Error parsing call data event:', { source, error });
      return undefined;
    }
  }, []);

  const sendDtmfTones = useCallback(
    async (digits: string) => {
      if (!connection) {
        console.warn('No hay conexión activa para enviar tonos DTMF');
        return;
      }

      try {
        connection.sendDigits(digits);
      } catch (error) {
        console.error('Error enviando tonos DTMF:', error);
      }
    },
    [connection],
  );

  const handleCallReceived = useCallback(
    (data: string) => {
      const parsedData = parseCallDataEvent(data, 'call-received');
      if (!parsedData) return;

      const { agentId, direction } = parsedData;

      if (agentId === myProfile?.id) {
        if (!getValidCallRouteId(parsedData.callId)) {
          console.warn(
            'Call data missing valid CRM call UUID',
            getCallAssignmentDiagnostics(parsedData, 'call-received'),
          );
        }

        setCallData(parsedData);
        if (direction === 'inbound') {
          setShowInboundCall(true);

          showCallNotification({
            callSid: parsedData.callSid,
            phoneNumber: parsedData.from,
          });

          playRingtone().then(audioStarted => {
            if (!audioStarted) {
              setPendingRingtone(true);
            }
          });
        } else {
          setCallDuration(Date.now());
          setIsCallInProgress(true);
        }
      }
    },
    [myProfile?.id, showCallNotification, playRingtone, setPendingRingtone, parseCallDataEvent],
  );

  const handleCallTransferAccepted = useCallback(
    (data: string) => {
      const parsedData = parseCallDataEvent(data, 'call-transfer:accepted');
      if (!parsedData) return;

      if (parsedData.agentId === myProfile?.id) {
        if (!getValidCallRouteId(parsedData.callId)) {
          console.warn(
            'Call transfer accepted without valid CRM call UUID',
            getCallAssignmentDiagnostics(parsedData, 'call-transfer:accepted'),
          );
        }

        setCallData(parsedData);
        if (!parsedData.caseId) openCallAssignmentDrawer(parsedData);
      } else {
        setIsSelectingCallCase(false);
      }
    },
    [myProfile?.id, openCallAssignmentDrawer, parseCallDataEvent],
  );

  const handleCallTransferCanceled = useCallback(
    (data: string) => {
      const parsedData = JSON.parse(data);
      if (parsedData.agentId === myProfile?.id) {
        stopRingtone();
        setShowInboundCall(false);
        setIsCallInProgress(false);
        setCallData(undefined);
        setConnection(undefined);
        setCallDuration(0);
      }
    },
    [myProfile?.id, stopRingtone],
  );

  const handleCallRoomClosed = useCallback(
    (data: string) => {
      const parsedData = JSON.parse(data);
      if (parsedData.callSid === callData?.callSid && connection) {
        stopRingtone();
        connection?.reject();
        setIsCallInProgress(false);
        setCallData(undefined);
        setConnection(undefined);
        setCallDuration(0);
        setCaseId(undefined);
        setClientData(undefined);
        setShowInboundCall(false);
        setShowOutgoingCallModal(false);
        setIsSelectingCallCase(false);
      }
    },
    [callData?.callSid, connection, stopRingtone],
  );

  useEffect(() => {
    if (deviceRef.current || !isAuthenticated) return;

    client.get('/calls/token').then(({ data }) => {
      deviceRef.current = new Device(data.token, {
        codecPreferences: [Call.Codec.PCMU, Call.Codec.PCMU],
      });

      deviceRef.current.register();
      deviceRef.current.audio?.incoming(false);
    });
  }, [isAuthenticated]);

  useEffect(() => {
    if (!deviceRef.current) return;

    deviceRef.current.on('tokenWillExpire', handleTokenWillExpire);
    deviceRef.current.on('incoming', handleIncomingCall);

    return () => {
      deviceRef.current?.off('tokenWillExpire', handleTokenWillExpire);
      deviceRef.current?.off('incoming', handleIncomingCall);
    };
  }, [deviceRef.current, handleTokenWillExpire, handleIncomingCall]);

  useEffect(() => {
    if (!userSocketRef.current) return;

    userSocketRef.current.on(WsEventsUsers.STATUS_UPDATED, handleStatusUpdated);
    userSocketRef.current.on(WsEventsUsers.AVAILABLE_AGENTS, handleAvailableAgents);

    return () => {
      if (userSocketRef.current) {
        userSocketRef.current.off(WsEventsUsers.STATUS_UPDATED, handleStatusUpdated);
        userSocketRef.current.off(WsEventsUsers.AVAILABLE_AGENTS, handleAvailableAgents);
      }
    };
  }, [userSocketRef.current, handleStatusUpdated, handleAvailableAgents]);

  useEffect(() => {
    if (!callsSocketRef.current || !myProfile?.id) return;

    callsSocketRef.current.on('call-received', handleCallReceived);
    callsSocketRef.current.on('call-transfer:accepted', handleCallTransferAccepted);
    callsSocketRef.current.on('call-transfer:canceled', handleCallTransferCanceled);
    callsSocketRef.current.on('call-room-closed', handleCallRoomClosed);
    callsSocketRef.current.on('outgoing-call:started', handleOutgoingCallStarted);

    return () => {
      if (callsSocketRef.current) {
        callsSocketRef.current.off('call-received', handleCallReceived);
        callsSocketRef.current.off('call-transfer:accepted', handleCallTransferAccepted);
        callsSocketRef.current.off('call-transfer:canceled', handleCallTransferCanceled);
        callsSocketRef.current.off('call-room-closed', handleCallRoomClosed);
        callsSocketRef.current.off('outgoing-call:started', handleOutgoingCallStarted);
      }
    };
  }, [
    callsSocketRef.current,
    myProfile?.id,
    handleCallReceived,
    handleCallTransferAccepted,
    handleCallTransferCanceled,
    handleCallRoomClosed,
  ]);

  useEffect(() => stopRingtone, [stopRingtone]);

  useEffect(() => {
    if (!showInboundCall) {
      stopRingtone();
    }
  }, [showInboundCall, stopRingtone]);

  return (
    <TwilioContext.Provider
      value={{
        callsSocketRef: callsSocketRef.current,
        connection,
        callDuration,
        callData,
        connectedAgents,
        caseId,
        handleCall: handleMakeCall,
        handleOpenOutgoingCallModal: toggleOutgoingCallModal(true),
        closeInProgressCall,
        updateCallData,
        isTransferCompleted: false,
        isCallTransferMode: false,
        sendDtmfTones,
      }}
    >
      <audio
        ref={audioRef}
        preload="auto"
        style={{ display: 'none' }}
        muted={!hasAudioPermission}
      >
        <source src={phoneRingSound} type="audio/mp3" />
        Tu navegador no soporta el elemento de audio.
      </audio>

      {showOutgoingCallModal && (
        <OutgoingCallModal
          open={showOutgoingCallModal}
          onClose={toggleOutgoingCallModal(false)}
          clientData={clientData}
        />
      )}
      {showInboundCall && <IncomingCallModal open={showInboundCall} onAccept={handleAnswerIncomingCall} />}
      {isCallInProgress && <FloatingCallWindow />}
      {isSelectingCallCase && (
        <CallAssignmentDrawer open={isSelectingCallCase} onClose={handleCloseCallAssignmentDrawer} />
      )}
      {children}
    </TwilioContext.Provider>
  );
};

export const useTwilio = () => {
  const ctx = useContext(TwilioContext);

  if (!ctx) throw new Error('useTwilio must be inside TwilioContextProvider');

  return ctx;
};
